"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Trophy,
  Users,
  Shield,
  Target,
  ArrowLeft,
  Vote,
  Plus,
  Edit2,
  Zap,
} from "lucide-react";
import { PlayerAvatar } from "@/components/shared/player-avatar";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import type {
  Hlasovanie,
  PravidloPozicie,
  VysledokSlotu,
  PoziciaHraca,
} from "@/types/database";
import { cn } from "@/lib/utils";

interface VysledkyDetailContentProps {
  hlasovanie: Hlasovanie;
  pravidla: PravidloPozicie[];
  mojeVybery: VysledokSlotu[];
  userId: string | null;
}

interface SlotData {
  pozicia: PoziciaHraca;
  slot: number;
  mojVyber: VysledokSlotu | null;
}

const EMPTY_SLOT_CARD_CLASSES =
  "group relative h-full min-h-[100px] border-dashed border-2 border-muted hover:border-primary/40 hover:bg-muted/50 cursor-pointer transition-all duration-300 flex flex-col items-center justify-center p-4";

const STAT_CARD_GRID_CLASSES =
  "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-6 bg-background/50";

export function VysledkyDetailContent({
  hlasovanie,
  pravidla,
  mojeVybery,
  userId,
}: VysledkyDetailContentProps) {
  const router = useRouter();

  const getPoziciaIcon = (pozicia: PoziciaHraca) => {
    switch (pozicia) {
      case "brankar":
        return <Shield className="h-5 w-5" />;
      case "obranca":
        return <Users className="h-5 w-5" />;
      case "utocnik":
        return <Target className="h-5 w-5" />;
    }
  };

  const getPoziciaLabel = (pozicia: PoziciaHraca) => {
    switch (pozicia) {
      case "brankar":
        return "Brankári";
      case "obranca":
        return "Obrancovia";
      case "utocnik":
        return "Útočníci";
    }
  };

  // Vytvorenie slot štruktúry
  const slotyData: SlotData[] = [];
  pravidla.forEach((pravidlo) => {
    for (let i = 1; i <= pravidlo.pocet_slotov; i++) {
      const mojVyber = mojeVybery.find(
        (v) => v.pozicia === pravidlo.pozicia && v.slot === i,
      );

      slotyData.push({
        pozicia: pravidlo.pozicia,
        slot: i,
        mojVyber: mojVyber || null,
      });
    }
  });

  // Zoskupenie slotov podľa pozícií
  const slotyPodlaPozicii = pravidla.map((pravidlo) => {
    const slotyPozicie = slotyData.filter(
      (s) => s.pozicia === pravidlo.pozicia,
    );
    const pocetVyplnenych = slotyPozicie.filter((s) => s.mojVyber).length;

    return {
      pravidlo,
      sloty: slotyPozicie,
      pocetVyplnenych,
    };
  });

  const handleSlotClick = () => {
    router.push(`/hlasovanie/${hlasovanie.id}`);
  };

  return (
    <div className="container mx-auto px-4 py-8 md:px-8 max-w-7xl space-y-6">
      {/* Navigačný panel */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" asChild className="gap-2">
          <Link href="/vysledky">
            <ArrowLeft className="h-4 w-4" />
            Všetky výsledky
          </Link>
        </Button>
        {userId && (
          <Button variant="outline" size="sm" asChild className="gap-2">
            <Link href={`/hlasovanie/${hlasovanie.id}`}>
              <Vote className="h-4 w-4" />
              Hlasovať
            </Link>
          </Button>
        )}
      </div>

      {/* Header */}
      <div className="space-y-3">
        <Badge variant="outline" className="px-3 py-1">
          Výsledky fanúšikovskej ankety
        </Badge>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
          {hlasovanie.nazov}
        </h1>
        {userId ? (
          <>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Trophy className="h-4 w-4 text-status-warning" />
                <span className="font-medium">Vaša nominácia</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-status-info" />
                <span className="font-medium">
                  {mojeVybery.length} vybraných hráčov
                </span>
              </div>
            </div>
            {userId && (
              <div className="flex items-center justify-between gap-3 p-3 bg-muted/50 border border-muted/20 rounded-lg">
                <p className="text-sm font-medium text-primary">
                  Zobrazujú sa vaše výbery ({mojeVybery.length} hráčov)
                </p>
                <Button variant="outline" size="sm" asChild className="gap-2">
                  <Link href={`/hlasovanie/${hlasovanie.id}`}>
                    <Edit2 className="h-3.5 w-3.5" />
                    Upraviť nomináciu
                  </Link>
                </Button>
              </div>
            )}
          </>
        ) : (
          <Card className="border-muted/60 bg-muted/20">
            <CardContent className="text-center py-8">
              <Vote className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                Prihláste sa pre zobrazenie vašej nominácie
              </h3>
              <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                Pre zobrazenie a úpravu vašej osobnej nominácie na ZOH 2026 sa
                musíte prihlásiť.
              </p>
              <Button asChild>
                <Link href={`/prihlasenie?callback=/vysledky/${hlasovanie.id}`}>
                  Prihlásiť sa
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Sloty podľa pozícií - len pre prihlásených */}
      {userId && (
        <Accordion
          type="multiple"
          defaultValue={pravidla.map((p) => p.id)}
          className="space-y-4"
        >
          {slotyPodlaPozicii.map(({ pravidlo, sloty, pocetVyplnenych }) => (
            <motion.div
              key={pravidlo.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <AccordionItem
                value={pravidlo.id}
                className="border-muted/60 shadow-sm bg-card rounded-xl overflow-hidden px-0"
              >
                <AccordionTrigger className="hover:no-underline px-6 py-4 bg-muted/20">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-background rounded-lg border shadow-sm text-primary">
                      {getPoziciaIcon(pravidlo.pozicia)}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center items-start gap-1 sm:gap-2">
                      <span className="font-bold text-sm sm:text-base">
                        {getPoziciaLabel(pravidlo.pozicia)}
                      </span>
                      {userId && (
                        <span className="text-xs text-muted-foreground font-medium">
                          Vybrané {pocetVyplnenych} z {pravidlo.pocet_slotov}
                        </span>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-0">
                  <div className={STAT_CARD_GRID_CLASSES}>
                    <AnimatePresence mode="popLayout">
                      {sloty.map((slotData) => (
                        <motion.div
                          key={`${slotData.pozicia}-${slotData.slot}`}
                          layout
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                        >
                          {slotData.mojVyber ? (
                            <Card className="group relative border-muted/60 shadow-sm hover:shadow-md transition-all duration-300">
                              <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                  <PlayerAvatar
                                    hracId={slotData.mojVyber.hrac_id}
                                    meno={slotData.mojVyber.hrac_meno}
                                    pozicia={slotData.mojVyber.pozicia}
                                    className="h-10 w-10 border shadow-sm shrink-0"
                                    fallbackClassName="text-xs"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-bold text-sm truncate group-hover:text-primary transition-colors">
                                      {slotData.mojVyber.hrac_meno}
                                    </h4>
                                    <p className="text-xs text-muted-foreground truncate font-medium">
                                      {slotData.mojVyber.klub || "Bez klubu"}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-4">
                                  <div className="flex gap-1.5">
                                    {/* Štatistiky alebo hodnota */}
                                    {slotData.mojVyber.sezona_body !== null &&
                                    slotData.mojVyber.sezona_body !==
                                      undefined ? (
                                      <Badge
                                        variant="secondary"
                                        className="text-[10px] px-1.5 h-5 bg-status-success/10 text-status-success border-none font-bold"
                                      >
                                        <Zap className="h-2.5 w-2.5 mr-1" />
                                        {slotData.mojVyber.sezona_body}B
                                      </Badge>
                                    ) : slotData.mojVyber.hodnota_hraca !==
                                        null &&
                                      slotData.mojVyber.hodnota_hraca !==
                                        undefined &&
                                      slotData.mojVyber.hodnota_hraca > 0 ? (
                                      <Badge
                                        variant="secondary"
                                        className="text-[10px] px-1.5 h-5 bg-status-info/10 text-status-info border-none font-bold"
                                      >
                                        €
                                        {(
                                          slotData.mojVyber.hodnota_hraca /
                                          1000000
                                        ).toFixed(1)}
                                        M
                                      </Badge>
                                    ) : slotData.mojVyber.vek_k_ms ? (
                                      <Badge
                                        variant="secondary"
                                        className="text-[10px] px-1.5 h-5 bg-muted text-muted-foreground border-none font-bold"
                                      >
                                        {slotData.mojVyber.vek_k_ms}r
                                      </Badge>
                                    ) : null}
                                    <Badge
                                      variant="secondary"
                                      className="text-[10px] px-1.5 h-5 bg-muted/50 font-bold uppercase tracking-wider border-none"
                                    >
                                      Slot {slotData.slot}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
                                      onClick={handleSlotClick}
                                    >
                                      <Edit2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ) : (
                            <Card
                              className={cn(
                                EMPTY_SLOT_CARD_CLASSES,
                                "flex flex-col justify-between",
                              )}
                              onClick={handleSlotClick}
                            >
                              <div className="flex flex-col items-center justify-center flex-1 py-2">
                                <div className="w-8 h-8 rounded-full bg-muted group-hover:bg-primary/10 flex items-center justify-center mb-2 transition-colors">
                                  <Plus className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                                </div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground group-hover:text-primary transition-colors">
                                  Slot {slotData.slot}
                                </p>
                                <p className="text-[8px] text-muted-foreground mt-1 text-center">
                                  Kliknite pre pridanie hráča
                                </p>
                              </div>
                            </Card>
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </motion.div>
          ))}
        </Accordion>
      )}
    </div>
  );
}
